import React from 'react';

/*
  MarketPage — "The Market" page.
  Houses non-finance AI tools that are outside the core Finance product.
  Props: navigateTo, goBack, user
*/

const TOOLS = [
  {
    id: 'legal-gpt',
    emoji: '⚖️',
    accentColor: '#8B5CF6',
    accentBg: 'rgba(139,92,246,0.10)',
    name: 'Legal GPT',
    desc: 'Get clear answers to your legal questions, India-specific.',
    route: 'legal-gpt',
  },
  {
    id: 'career-co-pilot',
    emoji: '🚀',
    accentColor: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.10)',
    name: 'Career Co-Pilot',
    desc: 'Navigate your next career move with AI guidance.',
    route: 'career-co-pilot',
  },
  {
    id: 'exit-engineer',
    emoji: '🏁',
    accentColor: '#F97316',
    accentBg: 'rgba(249,115,22,0.10)',
    name: 'Exit Engineer',
    desc: 'Plan your startup exit strategy with precision.',
    route: 'exit-engineer',
  },
  {
    id: 'decision-intelligence',
    emoji: '🧠',
    accentColor: '#14B8A6',
    accentBg: 'rgba(20,184,166,0.10)',
    name: 'Decision Intelligence',
    desc: 'Make complex business decisions with AI analysis.',
    route: 'decision-intelligence',
  },
  {
    id: 'customer-insights',
    emoji: '📊',
    accentColor: '#EC4899',
    accentBg: 'rgba(236,72,153,0.10)',
    name: 'Customer Insights',
    desc: 'Understand your customers deeply with AI.',
    route: 'customer-insights',
  },
  {
    id: 'ask-axigon-finance',
    emoji: '💬',
    accentColor: '#10B981',
    accentBg: 'rgba(16,185,129,0.10)',
    name: 'Ask Axigon Finance',
    desc: 'One AI advisor for your money, career, and legal questions — grounded in your uploaded data.',
    route: 'life-finance',
  },
];

const MarketPage = ({ navigateTo, goBack, user }) => {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>

      {/* Back link */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '28px 24px 0' }}>
        <button
          onClick={() => goBack ? goBack() : navigateTo('company')}
          style={{
            background: 'none', border: 'none', color: 'var(--text-tertiary)',
            fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0,
            fontFamily: 'inherit', transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
        >← Back to Home</button>
      </div>

      {/* Page header */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 24px 56px' }}>
        <p style={{
          fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 14px',
        }}>The Market</p>
        <h1 style={{
          fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 700,
          color: 'var(--text-primary)', lineHeight: 1.2,
          margin: '0 0 16px', letterSpacing: '-0.025em',
        }}>The Market</h1>
        <p style={{
          fontSize: '17px', color: 'var(--text-secondary)', lineHeight: 1.65,
          margin: 0, maxWidth: '520px',
        }}>
          Explore specialized AI tools built for Indian professionals.
        </p>
      </div>

      {/* Tool grid */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px 96px' }}>
        <style>{`
          @media (max-width: 640px) {
            .market-grid { grid-template-columns: 1fr !important; }
          }
          @media (min-width: 641px) and (max-width: 1023px) {
            .market-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
        `}</style>
        <div
          className="market-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
          }}
        >
          {TOOLS.map((tool) => (
            <ToolTile key={tool.id} tool={tool} navigateTo={navigateTo} />
          ))}
        </div>
      </div>

    </div>
  );
};

const ToolTile = ({ tool, navigateTo }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      style={{
        backgroundColor: 'var(--bg)',
        border: `1px solid ${hovered ? 'var(--border-strong)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-card)',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: hovered ? 'var(--shadow-md)' : 'var(--shadow-card)',
        cursor: 'default',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon area */}
      <div style={{
        width: '48px', height: '48px', borderRadius: '12px',
        backgroundColor: tool.accentBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '24px', lineHeight: 1,
        border: `1px solid ${tool.accentColor}22`,
        flexShrink: 0,
      }}>
        {tool.emoji}
      </div>

      {/* Text */}
      <div style={{ flex: 1 }}>
        <h3 style={{
          fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)',
          margin: '0 0 8px', lineHeight: 1.3,
        }}>{tool.name}</h3>
        <p style={{
          fontSize: '13px', color: 'var(--text-secondary)',
          lineHeight: 1.6, margin: 0,
        }}>{tool.desc}</p>
      </div>

      {/* CTA button */}
      <button
        onClick={() => navigateTo(tool.route)}
        style={{
          alignSelf: 'flex-start',
          backgroundColor: 'var(--accent)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          padding: '9px 18px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background-color 0.2s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--accent-hover)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--accent)'}
      >Open →</button>
    </div>
  );
};

export default MarketPage;
